/*  ========================================================================
    # Sections - Testimonials
    ========================================================================  */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import useLocalization from '@/react/hooks/useLocalization';
import bem from '@/react/utils/bem';
import LazyImage from '@/react/components/LazyImage';
import LazySource from '@/react/components/LazySource';


const Testimonials = ({ testimonials, mediaType }) => {
  const [activeSlide, setActiveSlide] = useState(0);

  // Icons
  const localize = useLocalization();
  const iconChevronLeft = `${localize('svgSprite')}#angle-left`;
  const iconChevronRight = `${localize('svgSprite')}#angle-right`;

  // Slide offset
  const getSlideOffset = (index) => {
    if (activeSlide === index) return 0;
    if (activeSlide === 0 && index === testimonials.length - 1) return -1;
    if (activeSlide < index || (activeSlide === testimonials.length - 1 && index === 0)) return 1;
    if (activeSlide > index) return -1;
  }

  // Navigation
  const handleSlideChange = (index) => {
    setActiveSlide(index);
  };

  const handleNavigateLeft = () => {
    if (activeSlide === 0) {
      setActiveSlide(testimonials.length - 1);
    } else {
      setActiveSlide(activeSlide - 1);
    }
  };

  const handleNavigateRight = () => {
    if (activeSlide === testimonials.length - 1) {
      setActiveSlide(0);
    } else {
      setActiveSlide(activeSlide + 1);
    }
  };

  // Swipehandler
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNavigateRight(),
    onSwipedRight: () => handleNavigateLeft(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  return (
    <>
      <div className="container">
        <div className={bem('s-testimonials', 'wrapper')}>
          <div className={bem('s-testimonials', 'controls')}>
            <button type='button' title='Nach links navigieren' className={bem('s-testimonials', 'control-left')} onClick={() => handleNavigateLeft()}>
              <svg viewBox='0 0 20 20'>
                <use href={iconChevronLeft} />
              </svg>
            </button>
            <button type='button' title='Nach rechts navigieren' className={bem('s-testimonials', 'control-right')} onClick={() => handleNavigateRight()}>
              <svg viewBox='0 0 20 20'>
                <use href={iconChevronRight} />
              </svg>
            </button>
          </div>
          <div className={bem('s-testimonials', 'slider')} {...swipeHandlers}>
            {testimonials.map((testimonial, index) => (
              <div key={index} className={bem('s-testimonials', 'slide', { horizontal: mediaType == 'horizontal' ? true : false })} data-offset={getSlideOffset(index)}>
                {mediaType != 'horizontal' ? (
                  <div className={bem('s-testimonials', 'icon')}>
                    <svg viewBox='0 0 20 20'>
                      <use href={`${localize('svgSprite')}#quote`} />
                    </svg>
                  </div>
                ) : null}
                <p className={bem('s-testimonials', 'quote')}>{testimonial.quote}</p>
                <div className={bem('s-testimonials', 'person')}>
                  {testimonial.image ? (
                    <LazyImage
                      defaultUrl={mediaType == 'horizontal' ? testimonial.image.sizes.medium : testimonial.image.sizes.thumbnail}
                      alt={testimonial.image.alt}
                      figureClass={bem('s-testimonials', 'image')}
                      imageClass='u-image-cover'
                    >
                      <LazySource mediaMin='1200px' srcSet={testimonial.image.sizes.medium_large} />
                    </LazyImage>
                  ) : null}
                  <div>
                    <p className={bem('s-testimonials', 'name')}>{testimonial.person.name}</p>
                    <p className={bem('s-testimonials', 'position')}>{testimonial.person.position}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className={bem('s-testimonials', 'indicators')}>
            {testimonials.map((testimonial, index) => (
              <button
                key={index}
                type='button'
                title={`Zum Slide ${index + 1} navigieren`}
                className={bem('s-testimonials', 'indicator', { active: index === activeSlide })}
                onClick={() => handleSlideChange(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

Testimonials.propTypes = {
  slides: PropTypes.array,
  mediaType: PropTypes.string
};

export default Testimonials;
